// components/TransfertTitres/SecurityTransferModal.jsx
import { Modal } from 'react-bootstrap';
import { PlusCircle, MinusCircle, Loader2, X } from 'lucide-react';
import '../style/SecurityTransferModal.css';
import { useSecurityTransfer } from '../../../service/Hooks/TransfertTitre/useSecurityTransfer';
import { useState } from 'react';
import TransferSecurityForm from './TransferSecurityForm';
import SecurityTransferConfirmationModal from './SecurityTransferConfirmationModal';

const SecurityTransferModal = ({
  show,
  onHide,
  accountId,
  accountDisplayName,
  domainId,
  onSuccess,
}) => {
  const [operationType, setOperationType] = useState('add'); // 'add' ou 'remove'
  const [showConfirmation, setShowConfirmation] = useState(false); // État pour le modal de confirmation
  
  const {
    formData,
    errors,
    isLoading,
    securities,
    updateField,
    handleSubmit,
    resetForm,
  } = useSecurityTransfer(accountId, domainId, () => {
    onSuccess?.();
    onHide();
  });

  const handleClose = () => {
    resetForm();
    setShowConfirmation(false);
    onHide();
  };

  // Fonction pour valider le formulaire avant d'afficher la confirmation
  const validateAndShowConfirmation = () => {
    // Vérifier les erreurs
    if (Object.keys(errors).length > 0) {
      // Afficher un message d'erreur pour chaque champ
      Object.values(errors).forEach(error => {
        if (error) toast.error(error);
      });
      return;
    }

    // Vérifier les champs obligatoires
    const requiredFields = ['securityId', 'quantity', 'preuve'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Vérifier la quantité
    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      toast.error('La quantité doit être supérieure à 0');
      return;
    }

    // Tout est valide, afficher le modal de confirmation
    setShowConfirmation(true);
  };

  // Fonction pour exécuter la soumission après confirmation
  const handleConfirmSubmit = () => {
    setShowConfirmation(false);
    handleSubmit(operationType === 'add');
  };

  const updateFieldHandler = (field, value) => {
    updateField(field, value);
  };

  return (
    <>
      {/* Modal de confirmation */}
      <SecurityTransferConfirmationModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        onConfirm={handleConfirmSubmit}
        operationType={operationType}
        quantity={formData.quantity}
        securityName={securities.find(s => s.value === formData.securityId)?.label}
        accountName={accountDisplayName}
        isLoading={isLoading}
      />
      
      {/* Modal principal */}
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Transfert de titres</h5>
                <small className="text-muted">Compte: {accountDisplayName}</small>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                disabled={isLoading}
                aria-label="Fermer"
              />
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Sélection du type d'opération */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Type d'opération</label>
            <div className="d-flex gap-3">
              <button
                type="button"
                className={`btn btn-operation ${operationType === 'add' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setOperationType('add')}
                disabled={isLoading}
              >
                <PlusCircle size={20} className="me-2" />
                Ajouter des titres
              </button>
              
              <button
                type="button"
                className={`btn btn-operation ${operationType === 'remove' ? 'btn-danger' : 'btn-outline-danger'}`}
                onClick={() => setOperationType('remove')}
                disabled={isLoading}
              >
                <MinusCircle size={20} className="me-2" />
                Retirer des titres
              </button>
            </div>
          </div>

          {/* Formulaire */}
          <TransferSecurityForm
            formData={formData}
            errors={errors}
            isLoading={isLoading}
            securities={securities}
            onFieldChange={updateFieldHandler}
            operationType={operationType}
            accountDisplayName={accountDisplayName}
          />
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Annuler
          </button>
          
          <button
            type="button"
            className={`btn ${operationType === 'add' ? 'btn-primary' : 'btn-danger'}`}
            onClick={validateAndShowConfirmation}
            disabled={isLoading || !formData.securityId || !formData.quantity || !formData.preuve}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="me-2 spin" />
                Traitement en cours...
              </>
            ) : operationType === 'add' ? (
              'Ajouter les titres'
            ) : (
              'Retirer les titres'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SecurityTransferModal;